import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Plus, Trash2, RefreshCw, X, AlertTriangle } from 'lucide-react';
import { itemUnitsService } from '../../../../../services/itemUnitsService';
import { itemsService } from '../../../../../services/itemsService';
import { ItemUnitsDto } from '../../../../../types/itemUnits';
import { useAuth } from '../../../../../contexts/AuthContext';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { useBarcodeRegistrationEnabled } from '../../../../../hooks/useBarcodeRegistrationEnabled';
import { WarehouseTable, useInlineEdit } from '../../../../shared/table';
import { createColumnFactory } from '../../../../shared/table/utils/tableUtils';
import type { ColumnDefinition, TableAction } from '../../../../shared/table/types/table';
import { NumericInput, ScanTextInput } from '../../../../common/inputs';

interface ItemUnitsDataTableProps {
  refreshTrigger?: number;
}

// WarehouseTable krever `id` på raden — ItemUnits har identity-kolonnen ID
type ItemUnitsRow = ItemUnitsDto & { id: number };

interface CreateForm {
  itemNo: string;
  itemBarCode: string;
  itemUnitCode: string;
  itemUnitTxt: string;
  unitFactor: string;
}

const EMPTY_FORM: CreateForm = { itemNo: '', itemBarCode: '', itemUnitCode: '', itemUnitTxt: '', unitFactor: '' };

/**
 * Samme GTIN-13/14-normalisering som backend (BarcodeUtils.Variants): 13 siffer → også med
 * ledende null, 14 siffer med ledende null → også uten. Brukes for å finne en eksisterende rad
 * før brukeren trykker Opprett.
 */
function barcodeVariants(barcode: string): string[] {
  const trimmed = barcode.trim();
  if (!trimmed) return [];
  const variants = [trimmed];
  if (/^\d+$/.test(trimmed)) {
    if (trimmed.length === 13) variants.push('0' + trimmed);
    else if (trimmed.length === 14 && trimmed.startsWith('0')) variants.push(trimmed.substring(1));
  }
  return variants;
}

/**
 * Grunndata → Enheter og strekkoder (ItemUnits) — TW 36415332.
 *
 * Viser alle alternative enheter/strekkoder per vare i firmaet, med inline-redigering
 * av enhetskode, tekst og faktor. Strekkoden er primærnøkkel og redigeres ikke inline —
 * slett og opprett på nytt. Opprettelse og sletting gates av items.allowBarcodeRegistration,
 * som i vareadmin. Tabellen fylles normalt av eSENTRAL via item-units-import.
 */
export const ItemUnitsDataTable: React.FC<ItemUnitsDataTableProps> = ({ refreshTrigger }) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const registrationEnabled = useBarcodeRegistrationEnabled();
  const companyNo = user?.companyNo || '';

  const [data, setData] = useState<ItemUnitsRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState<CreateForm>(EMPTY_FORM);
  const [createError, setCreateError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  // Visningsfiltre: kun foreldreløse rader, eller én bestemt strekkode («Gå til raden» fra dialogen)
  const [showOrphansOnly, setShowOrphansOnly] = useState(false);
  const [focusBarcode, setFocusBarcode] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!companyNo) return;
    setIsLoading(true);
    setError(null);
    try {
      const units = await itemUnitsService.getCurrentItemUnits();
      setData(units.map(u => ({ ...u, id: u.id })));
    } catch (err) {
      console.error('Error loading item units:', err);
      setError(t('manager.modules.adminHost.maintenance.itemUnits.loadError', {}, 'Kunne ikke laste enheter og strekkoder. Prøv igjen.'));
    } finally {
      setIsLoading(false);
    }
  }, [companyNo, t]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0) loadData();
  }, [refreshTrigger, loadData]);

  const inlineEditConfig = useInlineEdit<ItemUnitsRow>({
    getRowId: (row) => row.id,
    onSave: async (originalRow, edited) => {
      const unitCode = ((edited.itemUnitCode as string) ?? originalRow.itemUnitCode).trim();
      if (!unitCode) {
        throw new Error(t('manager.modules.adminHost.maintenance.itemUnits.unitCodeRequired', {}, 'Enhetskode er påkrevd'));
      }
      // Inline-editoren kan levere tekst selv om feltet er typet som tall — behandle som ukjent
      const factorRaw = (edited.unitFactor !== undefined ? edited.unitFactor : originalRow.unitFactor) as unknown;
      const factor = factorRaw === null || factorRaw === undefined || factorRaw === '' ? null : Number(factorRaw);
      if (factor !== null && (!Number.isFinite(factor) || factor <= 0)) {
        throw new Error(t('manager.modules.adminHost.maintenance.itemUnits.unitFactorInvalid', {}, 'Faktor må være et tall større enn 0'));
      }
      await itemUnitsService.updateItemUnit(companyNo, originalRow.itemBarCode, {
        itemUnitCode: unitCode,
        itemUnitTxt: ((edited.itemUnitTxt as string | undefined) ?? originalRow.itemUnitTxt ?? '').trim() || null,
        unitFactor: factor
      });
    },
    onSaveSuccess: () => loadData(),
    onSaveError: (err) => setError(err.message || t('manager.modules.adminHost.maintenance.itemUnits.saveError', {}, 'Feil ved lagring av enhet'))
  });

  const orphanCount = useMemo(() => data.filter(r => r.itemExists === false).length, [data]);

  const visibleData = useMemo(() => {
    if (focusBarcode) {
      return data.filter(r => r.itemBarCode === focusBarcode);
    }
    return showOrphansOnly ? data.filter(r => r.itemExists === false) : data;
  }, [data, showOrphansOnly, focusBarcode]);

  // Eksisterende rad for strekkoden i dialogen (eksakt eller GTIN-variant) — strekkoden er
  // primærnøkkel, så dette er en redigering av den raden, ikke en ny enhet.
  const existingForBarcode = useMemo(() => {
    const variants = barcodeVariants(createForm.itemBarCode);
    if (variants.length === 0) return null;
    return data.find(r => variants.some(v => v.toLowerCase() === r.itemBarCode.toLowerCase())) ?? null;
  }, [data, createForm.itemBarCode]);

  const goToExistingRow = () => {
    if (!existingForBarcode) return;
    setShowCreateModal(false);
    setShowOrphansOnly(false);
    setFocusBarcode(existingForBarcode.itemBarCode);
    setSelectedIds(registrationEnabled ? [existingForBarcode.id] : []);
  };

  const columnFactory = createColumnFactory<ItemUnitsRow>();

  const columns: ColumnDefinition<ItemUnitsRow>[] = useMemo(() => [
    columnFactory.createTextColumn('itemNo', 'itemNo', t('common.fields.itemNumber', {}, 'Varenr'), {
      minWidth: 140,
      sortable: true,
      filterType: 'both'
    }),
    columnFactory.createBooleanColumn('itemExists', 'itemExists', t('manager.modules.adminHost.maintenance.itemUnits.fields.itemExists', {}, 'Vare finnes'), {
      minWidth: 100,
      sortable: true,
      filterType: 'dropdown',
      formatter: (value: any) => value === false
        ? t('manager.modules.adminHost.maintenance.itemUnits.itemMissing', {}, 'Mangler')
        : t('manager.modules.adminHost.maintenance.itemUnits.itemOk', {}, 'Ja')
    }),
    columnFactory.createTextColumn('itemBarCode', 'itemBarCode', t('manager.modules.adminHost.maintenance.itemUnits.fields.itemBarCode', {}, 'Strekkode'), {
      minWidth: 160,
      sortable: true,
      filterType: 'both'
    }),
    columnFactory.createTextColumn('itemUnitCode', 'itemUnitCode', t('manager.modules.adminHost.maintenance.itemUnits.fields.itemUnitCode', {}, 'Enhetskode'), {
      minWidth: 110,
      sortable: true,
      filterType: 'both',
      editable: registrationEnabled
    }),
    columnFactory.createTextColumn('itemUnitTxt', 'itemUnitTxt', t('manager.modules.adminHost.maintenance.itemUnits.fields.itemUnitTxt', {}, 'Enhetstekst'), {
      minWidth: 140,
      sortable: true,
      filterType: 'both',
      editable: registrationEnabled,
      formatter: (value: any) => value || '-'
    }),
    columnFactory.createNumberColumn('unitFactor', 'unitFactor', t('manager.modules.adminHost.maintenance.itemUnits.fields.unitFactor', {}, 'Faktor (antall per enhet)'), {
      minWidth: 120,
      sortable: true,
      editable: registrationEnabled,
      formatter: (value: any) => value != null ? String(value) : '-'
    }),
    columnFactory.createTextColumn('createdBy', 'createdBy', t('manager.modules.adminHost.warehouseApp.columns.createdBy', {}, 'Opprettet av'), {
      minWidth: 110,
      sortable: true,
      filterType: 'both',
      formatter: (value: any) => value || '-'
    }),
    columnFactory.createDateColumn('created', 'created', t('manager.modules.adminHost.warehouseApp.columns.created', {}, 'Opprettet'), {
      minWidth: 130,
      sortable: true
    }),
    columnFactory.createTextColumn('updatedBy', 'updatedBy', t('manager.modules.adminHost.warehouseApp.columns.updatedBy', {}, 'Oppdatert av'), {
      minWidth: 110,
      sortable: true,
      filterType: 'both',
      formatter: (value: any) => value || '-'
    }),
    columnFactory.createDateColumn('lastUpdate', 'lastUpdate', t('manager.modules.adminHost.warehouseApp.columns.lastUpdate', {}, 'Sist oppdatert'), {
      minWidth: 130,
      sortable: true
    })
  ], [t, registrationEnabled]);

  const handleDelete = useCallback(async () => {
    if (selectedIds.length === 0) return;
    const rows = data.filter(r => selectedIds.includes(r.id));
    const confirmed = window.confirm(
      t('manager.modules.adminHost.maintenance.itemUnits.confirmDelete', { '0': rows.length.toString() }, `Slette ${rows.length} enhet(er)/strekkode(r)?`)
    );
    if (!confirmed) return;

    const errors: string[] = [];
    for (const row of rows) {
      try {
        await itemUnitsService.deleteItemUnit(companyNo, row.itemBarCode);
      } catch (err: any) {
        errors.push(`${row.itemBarCode}: ${err.message}`);
      }
    }
    if (errors.length > 0) {
      setError(errors.join(' | '));
    }
    setSelectedIds([]);
    await loadData();
  }, [selectedIds, data, companyNo, loadData, t]);

  const tableActions: TableAction<ItemUnitsRow>[] = useMemo(() => {
    const actions: TableAction<ItemUnitsRow>[] = [];
    if (registrationEnabled) {
      actions.push({
        id: 'create',
        label: t('manager.modules.adminHost.maintenance.itemUnits.createTitle', {}, 'Ny enhet/strekkode'),
        icon: Plus,
        onClick: () => { setCreateForm(EMPTY_FORM); setCreateError(null); setShowCreateModal(true); },
        variant: 'primary' as const
      });
      actions.push({
        id: 'delete',
        label: t('manager.modules.adminHost.maintenance.itemUnits.delete', { '0': selectedIds.length.toString() }, `Slett (${selectedIds.length})`),
        icon: Trash2,
        onClick: () => handleDelete(),
        disabled: () => selectedIds.length === 0,
        variant: 'danger' as const
      });
    }
    actions.push({
      id: 'refresh',
      label: t('common.buttons.refresh', {}, 'Oppdater'),
      icon: RefreshCw,
      onClick: () => loadData(),
      disabled: () => isLoading,
      variant: 'secondary' as const
    });
    return actions;
  }, [registrationEnabled, selectedIds.length, handleDelete, loadData, isLoading, t]);

  const canCreate = createForm.itemNo.trim() && createForm.itemBarCode.trim() && createForm.itemUnitCode.trim();

  const handleCreate = async () => {
    if (!canCreate || isSaving) return;
    setIsSaving(true);
    setCreateError(null);

    const itemNo = createForm.itemNo.trim();
    const factor = createForm.unitFactor.trim() ? parseFloat(createForm.unitFactor) : null;
    if (factor !== null && (!Number.isFinite(factor) || factor <= 0)) {
      setCreateError(t('manager.modules.adminHost.maintenance.itemUnits.unitFactorInvalid', {}, 'Faktor må være et tall større enn 0'));
      setIsSaving(false);
      return;
    }

    try {
      // CRUD-endepunktet validerer ikke at varen finnes (det gjør bare importen) — sjekk her,
      // så en tastefeil i varenummeret ikke gir en foreldreløs enhet.
      try {
        await itemsService.getItemByCompanyAndItemNo(companyNo, itemNo);
      } catch {
        setCreateError(t('manager.modules.adminHost.maintenance.itemUnits.itemNotFound', { '0': itemNo }, `Varenummer '${itemNo}' finnes ikke`));
        return;
      }

      await itemUnitsService.createItemUnit({
        companyNo,
        itemNo,
        itemBarCode: createForm.itemBarCode.trim(),
        itemUnitCode: createForm.itemUnitCode.trim(),
        itemUnitTxt: createForm.itemUnitTxt.trim() || null,
        unitFactor: factor
      });
      setShowCreateModal(false);
      setCreateForm(EMPTY_FORM);
      await loadData();
    } catch (err: any) {
      // 409 fra backend bærer forklarende melding (strekkoden finnes på annen vare)
      setCreateError(err.message || t('manager.modules.adminHost.maintenance.itemUnits.saveError', {}, 'Feil ved lagring av enhet'));
    } finally {
      setIsSaving(false);
    }
  };

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg';

  return (
    <div className="flex flex-col h-full overflow-hidden bg-gray-50">
      {!registrationEnabled && (
        <div className="mx-2 mt-2 px-3 py-2 text-xs text-gray-600 bg-gray-100 border border-gray-200 rounded">
          {t('manager.modules.adminHost.maintenance.itemUnits.readOnlyHint', {}, 'Registrering av strekkoder er skrudd av (items.allowBarcodeRegistration). Tabellen er skrivebeskyttet.')}
        </div>
      )}

      {focusBarcode ? (
        <div className="mx-2 mt-2 px-3 py-2 text-sm bg-blue-50 border border-blue-200 rounded flex items-center justify-between gap-3">
          <span className="text-blue-800">
            {t('manager.modules.adminHost.maintenance.itemUnits.focusBanner', { '0': focusBarcode }, `Viser raden for strekkode ${focusBarcode}`)}
          </span>
          <button
            onClick={() => setFocusBarcode(null)}
            className="px-3 py-1 text-xs font-medium bg-white border border-blue-300 text-blue-700 rounded hover:bg-blue-100"
          >
            {t('manager.modules.adminHost.maintenance.itemUnits.showAll', {}, 'Vis alle')}
          </button>
        </div>
      ) : orphanCount > 0 && (
        <div className="mx-2 mt-2 px-3 py-2 text-sm bg-amber-50 border border-amber-200 rounded flex items-center justify-between gap-3">
          <span className="flex items-center gap-2 text-amber-800">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            {t('manager.modules.adminHost.maintenance.itemUnits.orphanBanner', { '0': orphanCount.toString() }, `${orphanCount} enhet(er) peker på et varenummer som ikke finnes i varekortet`)}
          </span>
          <button
            onClick={() => setShowOrphansOnly(v => !v)}
            className="px-3 py-1 text-xs font-medium bg-white border border-amber-300 text-amber-800 rounded hover:bg-amber-100 whitespace-nowrap"
          >
            {showOrphansOnly
              ? t('manager.modules.adminHost.maintenance.itemUnits.showAll', {}, 'Vis alle')
              : t('manager.modules.adminHost.maintenance.itemUnits.showOrphansOnly', {}, 'Vis kun disse')}
          </button>
        </div>
      )}

      <div className="flex-1 min-h-0">
        <WarehouseTable
          data={visibleData}
          columns={columns}
          getRowClassName={(row) => row.itemExists === false ? 'bg-red-50' : undefined}
          config={{
            showPagination: true,
            showColumnConfig: true,
            showGlobalSearch: true,
            showFilters: true,
            showExport: true,
            showPrint: false,
            showSummary: false,
            defaultPageSize: 100,
            allowColumnReorder: true,
            allowColumnResize: true,
            stickyHeader: true,
            compactMode: true,
            persistenceKey: 'item-units-table',
            allowRowSelection: registrationEnabled
              ? { mode: 'multi', selectAllMode: 'all-data' }
              : false
          }}
          selectedRows={selectedIds}
          onSelectionChange={(ids) => setSelectedIds(ids as number[])}
          getRowId={(row) => row.id}
          tableActions={tableActions}
          inlineEditConfig={registrationEnabled ? inlineEditConfig : undefined}
          loading={isLoading}
          error={error || undefined}
        />
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h3 className="text-lg font-semibold">
                {t('manager.modules.adminHost.maintenance.itemUnits.createTitle', {}, 'Ny enhet/strekkode')}
              </h3>
              <button onClick={() => setShowCreateModal(false)} className="p-2 rounded hover:bg-gray-100">
                <X size={18} />
              </button>
            </div>
            <div className="p-4 space-y-3">
              {createError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 break-words">{createError}</div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('common.fields.itemNumber', {}, 'Varenr')} *
                </label>
                <ScanTextInput
                  value={createForm.itemNo}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, itemNo: e.target.value }))}
                  className={inputClass}
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('manager.modules.adminHost.maintenance.itemUnits.fields.itemBarCode', {}, 'Strekkode')} *
                </label>
                <ScanTextInput
                  value={createForm.itemBarCode}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, itemBarCode: e.target.value }))}
                  className={`${inputClass} font-mono`}
                />
                {existingForBarcode && (
                  <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
                    <p>
                      {t('manager.modules.adminHost.maintenance.itemUnits.barcodeExists',
                        { '0': existingForBarcode.itemNo, '1': existingForBarcode.itemUnitCode },
                        `Strekkoden finnes allerede på vare ${existingForBarcode.itemNo} med enhetskode ${existingForBarcode.itemUnitCode}. Én strekkode kan bare ha én enhet — rediger den eksisterende raden i stedet.`)}
                    </p>
                    <button
                      type="button"
                      onClick={goToExistingRow}
                      className="mt-2 px-3 py-1.5 text-xs font-medium bg-white border border-amber-300 text-amber-900 rounded hover:bg-amber-100"
                    >
                      {t('manager.modules.adminHost.maintenance.itemUnits.goToRow', {}, 'Gå til raden')}
                    </button>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('manager.modules.adminHost.maintenance.itemUnits.fields.itemUnitCode', {}, 'Enhetskode')} *
                  </label>
                  <input
                    type="text"
                    maxLength={20}
                    value={createForm.itemUnitCode}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, itemUnitCode: e.target.value }))}
                    className={inputClass}
                    placeholder={t('manager.modules.adminHost.maintenance.itemUnits.unitCodePlaceholder', {}, 'f.eks. DPK, PALL')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('manager.modules.adminHost.maintenance.itemUnits.fields.unitFactor', {}, 'Faktor (antall per enhet)')}
                  </label>
                  <NumericInput
                    mode="decimal"
                    value={createForm.unitFactor}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, unitFactor: e.target.value }))}
                    className={inputClass}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('manager.modules.adminHost.maintenance.itemUnits.fields.itemUnitTxt', {}, 'Enhetstekst')}
                </label>
                <input
                  type="text"
                  maxLength={20}
                  value={createForm.itemUnitTxt}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, itemUnitTxt: e.target.value }))}
                  className={inputClass}
                />
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 rounded-b-lg flex justify-end gap-2">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
              >
                {t('common.buttons.cancel', {}, 'Avbryt')}
              </button>
              <button
                onClick={handleCreate}
                disabled={isSaving || !canCreate || !!existingForBarcode}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {isSaving
                  ? t('common.buttons.saving', {}, 'Lagrer...')
                  : t('common.buttons.create', {}, 'Opprett')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
